import { memo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import {
  AngleIcon,
  ExternalLinkIcon,
  SuccessIcon,
} from '../../../../components/Icons';
import { useMediaQueryContext } from '../../../../contexts/MediaQueryContext';
import { ShareButtons } from '../ShareButtons';

interface PublishedSuccessStateProps {
  twinId: string;
  personaSlug: string;
  isMacos: boolean;
}

const PublishedSuccessState: React.FC<PublishedSuccessStateProps> = memo(
  ({ twinId, personaSlug, isMacos }) => {
    const { t } = useTranslation();
    const { isSm } = useMediaQueryContext();

    return (
      <div className="h-full relative grow flex flex-col items-center justify-center px-0 sm:px-4 pt-4 sm:pt-10">
        <SuccessIcon size={isSm ? 76 : 118} className="text-white" />

        <h2 className="text-[24px] sm:text-[40px] mt-4 sm:mt-8 text-center font-seriftext">
          {t('twin.publishedTitle')}
        </h2>
        <div className="my-0 sm:my-14 flex gap-4 flex-col sm:flex-row w-full justify-center">
          <Menu>
            <MenuButton className="btn btn-primary focus:outline-none block">
              {t('twin.editYourTwin')}
              <div className="-rotate-90 ml-2">
                <AngleIcon />
              </div>
            </MenuButton>
            <MenuItems
              modal={false}
              portal={true}
              anchor="bottom"
              className="[--anchor-gap:8px] w-(--button-width) shadow-lg z-[99999] tc-mv-blur rounded-2xl p-2 focus:outline-none origin-top transition duration-200 ease-out data-closed:scale-95 data-closed:opacity-0"
              transition>
              <MenuItem
                as={Link}
                to="/twin/knowledge/files"
                className="w-full px-4 py-2 rounded-lg text-sm text-gray-200 hover:bg-black hover:bg-opacity-20 flex items-center transition-colors duration-150">
                {t('twin.knowledgeBase')}
              </MenuItem>
              <MenuItem
                as={Link}
                to="/twin/knowledge/faq"
                className="w-full px-4 py-2 rounded-lg text-sm text-gray-200 hover:bg-black hover:bg-opacity-20 flex items-center transition-colors duration-150">
                {t('twin.faq')}
              </MenuItem>
            </MenuItems>
          </Menu>

          <Link
            data-testid="published-success-talk-with-twin-link"
            className="btn btn-primary"
            to={
              isMacos
                ? `napster-companion://companion/start/${twinId}`
                : `/${personaSlug}?start=true`
            }>
            {t('twin.talkWithTwin')}&nbsp;
            <ExternalLinkIcon height={16} width={16} />
          </Link>
        </div>

        <h3 className="text-xl mt-8">{t('twin.shareYourTwin')}</h3>
        <p className="text-center max-w-[600px] mx-auto">
          {t('twin.shareDescription')}
        </p>

        <div className="mt-4">
          <ShareButtons slug={personaSlug} />
        </div>
      </div>
    );
  },
);

export { PublishedSuccessState };
